import React from 'react';
import Parse from 'parse';
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';

export default function SubscriberDirectory({ open, onClose }) {
  const [status, setStatus] = React.useState('all');
  const [cursors, setCursors] = React.useState(['']);
  const [result, setResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [retry, setRetry] = React.useState(0);
  const after = cursors[cursors.length - 1];
  React.useEffect(() => {
    if (!open) return undefined;
    let current = true;
    setLoading(true);
    setError('');
    Parse.Cloud.run('listSubscriberDirectory', { status, after, limit: 25 })
      .then((data) => {
        if (current) setResult(data);
      })
      .catch(() => {
        if (current) {
          setResult(null);
          setError('Your audience could not be loaded. Please try again.');
        }
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [open, status, after, retry]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="audience-directory-title"
    >
      <DialogTitle id="audience-directory-title">
        Followers & subscribers
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          View subscription status and information people submitted to you.
          Unsubscribed people do not receive notifications. Contact details and
          additional form answers are shown when available.
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
          <InputLabel id="audience-status-label">
            Subscription status
          </InputLabel>
          <Select
            labelId="audience-status-label"
            label="Subscription status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setCursors(['']);
              setResult(null);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Following</MenuItem>
            <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
          </Select>
        </FormControl>
        {error && (
          <Alert
            severity="error"
            action={
              <Button onClick={() => setRetry((value) => value + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {loading ? (
          <Stack direction="row" spacing={2} alignItems="center" role="status">
            <CircularProgress size={20} />
            <span>Loading your audience…</span>
          </Stack>
        ) : (
          result && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {result.total} {result.total === 1 ? 'person' : 'people'}
              </Typography>
              {!result.items.length ? (
                <Typography>No people match this status.</Typography>
              ) : (
                <TableContainer>
                  <Table
                    size="small"
                    aria-label="Followers and submitted information"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Person</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Channel</TableCell>
                        <TableCell>Submitted information</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.items.map((person) => (
                        <TableRow key={person.id}>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {person.responses
                              .flatMap((response) => response.fields)
                              .find(
                                (field) => field.label.toLowerCase() === 'name'
                              )?.value || person.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {person.email || 'Not provided'}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {person.active ? 'Following' : 'Unsubscribed'}
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            {(person.channels || [])
                              .map(
                                (channel) =>
                                  ({
                                    app: 'Mobile app',
                                    browser: 'Browser',
                                    legacy: 'Not recorded',
                                  }[channel] || 'Not recorded')
                              )
                              .join(', ')}
                          </TableCell>
                          <TableCell sx={{ minWidth: 250 }}>
                            {person.responses.length
                              ? person.responses.map((response, index) => (
                                  <details
                                    key={index}
                                    style={{ marginBottom: 10 }}
                                  >
                                    <summary style={{ cursor: 'pointer' }}>
                                      {response.sourceName}
                                      {response.submittedAt
                                        ? ` · ${new Date(
                                            response.submittedAt
                                          ).toLocaleDateString()}`
                                        : ''}
                                    </summary>
                                    <dl>
                                      {response.fields.map(
                                        (field, fieldIndex) => (
                                          <React.Fragment key={fieldIndex}>
                                            <dt style={{ fontWeight: 600 }}>
                                              {field.label}
                                            </dt>
                                            <dd
                                              style={{
                                                margin: '2px 0 12px',
                                                overflowWrap: 'anywhere',
                                                whiteSpace: 'pre-wrap',
                                              }}
                                            >
                                              {field.value || '—'}
                                            </dd>
                                          </React.Fragment>
                                        )
                                      )}
                                    </dl>
                                  </details>
                                ))
                              : 'No saved form responses'}
                            {person.responsesTruncated && (
                              <Typography variant="caption">
                                Showing the latest 100 responses.
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  disabled={cursors.length === 1}
                  onClick={() => setCursors((values) => values.slice(0, -1))}
                >
                  Previous
                </Button>
                <Button
                  disabled={!result.next}
                  onClick={() =>
                    setCursors((values) => [...values, result.next])
                  }
                >
                  Next
                </Button>
              </Stack>
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
